import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import { Tab } from "../../atoms/Tab";

import './TabMenu.scss';

export function TabMenu({ tabs, activeTab, onClick, orientation, ariaLabel }) {
    const { t } = useTranslation();
    const tabRefs = useRef(new Map());

    const handleTabClick = useCallback((tabId) => {
        if (typeof onClick === 'function') {
            onClick(tabId);
        }
    }, [onClick]);

    const registerTab = useCallback((tabId) => (node) => {
        if (node) {
            tabRefs.current.set(tabId, node);
        } else {
            tabRefs.current.delete(tabId);
        }
    }, []);

    const handleKeyDown = useCallback((event, index) => {
        if (!tabs.length) {
            return;
        }

        const lastIndex = tabs.length - 1;
        let targetIndex = null;

        if (orientation === 'vertical') {
            if (event.key === 'ArrowDown') {
                targetIndex = index === lastIndex ? 0 : index + 1;
            }
            if (event.key === 'ArrowUp') {
                targetIndex = index === 0 ? lastIndex : index - 1;
            }
        } else {
            if (event.key === 'ArrowRight') {
                targetIndex = index === lastIndex ? 0 : index + 1;
            }
            if (event.key === 'ArrowLeft') {
                targetIndex = index === 0 ? lastIndex : index - 1;
            }
        }

        if (event.key === 'Home') {
            targetIndex = 0;
        }

        if (event.key === 'End') {
            targetIndex = lastIndex;
        }

        if (targetIndex == null) {
            return;
        }

        event.preventDefault();

        const targetTab = tabs[targetIndex];
        if (!targetTab) {
            return;
        }

        const node = tabRefs.current.get(targetTab.id);
        if (node && typeof node.focus === 'function') {
            node.focus();
        }
    }, [orientation, tabs]);

    return (
        <div
            className={classNames('tab-menu', `tab-menu--${orientation}`)}
            role="tablist"
            aria-label={ariaLabel}
            aria-orientation={orientation}
        >
            {tabs.map((tab, index) => {
                const Icon = tab.icon;
                const handlePrefetch = typeof tab.onPrefetch === 'function'
                    ? () => {
                        try {
                            tab.onPrefetch();
                        } catch (error) {
                            console.error('Tab prefetch failed:', error);
                        }
                    }
                    : undefined;
                const tabLabel = tab.labelKey ? t(tab.labelKey) : tab.label;
                return (
                    <Tab
                        key={tab.id}
                        id={tab.id}
                        icon={Icon}
                        className={classNames(
                            'tab',
                            `tab--${orientation}`,
                            { active: activeTab === tab.id }
                        )}
                        onClick={() => handleTabClick(tab.id)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        label={tabLabel}
                        badge={tab.badge}
                        badgeAriaLabel={tab.badgeAriaLabel}
                        badgeAriaLabelKey={tab.badgeAriaLabelKey}
                        isActive={activeTab === tab.id}
                        orientation={orientation}
                        onFocus={handlePrefetch}
                        onMouseEnter={handlePrefetch}
                        ref={registerTab(tab.id)}
                    />
                );
            })}
        </div>
    );
}

TabMenu.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.node,
        labelKey: PropTypes.string,
        icon: PropTypes.elementType,
        badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        badgeAriaLabel: PropTypes.string,
        badgeAriaLabelKey: PropTypes.string,
        onPrefetch: PropTypes.func,
    })).isRequired,
    activeTab: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    orientation: PropTypes.oneOf(['vertical', 'horizontal']),
    ariaLabel: PropTypes.string,
};

TabMenu.defaultProps = {
    onClick: undefined,
    orientation: 'vertical',
    ariaLabel: undefined,
};
